import React, { useMemo, useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { PageWrapper } from "../../../../shared/components/layout/PageWrapper";
import { ContextInfoCard } from "../../../../shared/components/ui/ContextInfoCard";
import { ROUTES } from "../../../../routes/routes";
import { RoleEnum } from "../../../../api/client/types/user.types";
import { useAuthStore } from "../../../../store";
import { EditSwimmerForm } from "../../components/EditSwimmerForm/EditSwimmerForm";
import { useEditSwimmerPage } from "./useEditSwimmerPage";

/**
 * EditSwimmerPage
 * Page to edit an existing swimmer (bañista). Used by admins and lifeguards.
 * - Admin: shows full context (lifeguard, building, city, age, responsible).
 * - Lifeguard: shows only "Responsable" in the context card.
 */
export const EditSwimmerPage: React.FC = () => {
  const { swimmerId } = useParams<{ swimmerId: string }>();
  const { user } = useAuthStore();
  const {
    swimmer,
    swimmerTypes,
    initialData,
    handleFormSubmit,
    handleNavigateToDashboard,
    isSubmitting,
    isLoadingSwimmer,
    isErrorSwimmer,
  } = useEditSwimmerPage();

  const isAdmin = user?.role === RoleEnum.ADMINISTRATOR;

  const [registrationTimestamp, setRegistrationTimestamp] = React.useState<
    string | null
  >(null);

  useEffect(() => {
    if (swimmer?.registration_timestamp) {
      setRegistrationTimestamp(swimmer.registration_timestamp);
    }
  }, [swimmer?.registration_timestamp]);

  // Ensure the page always opens scrolled to top (avoids restored scroll from nav/history)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [swimmerId]);

  const contextItems = useMemo(() => {
    if (!swimmer) return [];
    const isLifeguard = user?.role === RoleEnum.LIFEGUARD;
    if (isLifeguard) {
      return [{ label: "Responsable", value: swimmer.responsible?.name }];
    }
    return [
      { label: "Salvavidas de turno", value: swimmer.lifeguard_name },
      { label: "Unidad residencial", value: swimmer.building_name },
      { label: "Ciudad", value: swimmer.city },
      {
        label: "Clasificacion de edad",
        value: swimmer.age_classification?.name,
      },
      { label: "Responsable", value: swimmer.responsible?.name },
    ];
  }, [swimmer, user?.role]);

  if (!swimmerId) {
    const isLifeguard = user?.role === RoleEnum.LIFEGUARD;
    const dashboard = isLifeguard
      ? ROUTES.DASHBOARD.LIFEGUARD
      : ROUTES.DASHBOARD.ADMINISTRATOR;
    return <Navigate to={dashboard} replace />;
  }

  return (
    <PageWrapper
      isLoading={isLoadingSwimmer}
      loadingLabel="Cargando bañista..."
      isError={isErrorSwimmer}
      errorMessage="No se pudo cargar el bañista. Puede que no exista o no tenga permiso para editarlo."
      onBack={handleNavigateToDashboard}
      title="Editar Bañista"
    >
      {swimmer && initialData ? (
        <div className="flex flex-col gap-6 w-full max-w-4xl mx-auto">
          <ContextInfoCard title="Contexto de bañista" items={contextItems} />
          <EditSwimmerForm
            onSubmit={(data) =>
              handleFormSubmit(data, isAdmin ? registrationTimestamp : undefined)
            }
            swimmerTypes={swimmerTypes}
            initialData={initialData}
            isSubmitting={isSubmitting}
            submitLabel="Guardar cambios"
            canEditRegistrationTimestamp={isAdmin}
            initialRegistrationTimestamp={swimmer.registration_timestamp ?? null}
            onRegistrationTimestampChange={setRegistrationTimestamp}
          />
        </div>
      ) : null}
    </PageWrapper>
  );
};
